
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, Calendar, DollarSign } from "lucide-react";
import { format } from "date-fns";
import { Project } from "@/types/Project";
import { useIsMobile } from "@/hooks/use-mobile";

interface StatsCardsProps {
  stats: {
    totalHours: number;
    totalMinutes: number;
    todayHours: number;
    todayMinutes: number;
    totalEntries: number;
    projectStats: Record<string, number>;
    totalBilling: number;
    hourlyRate: number;
    selectedProject?: Project;
  };
  projects: Project[];
}

export function StatsCards({ stats, projects }: StatsCardsProps) {
  const isMobile = useIsMobile();
  
  return (
    <div className={`grid ${isMobile ? "grid-cols-1 gap-3" : "grid-cols-1 sm:grid-cols-3 gap-5"}`}>
      <Card className="hover-scale shadow-md border-t-2 border-t-primary/20">
        <CardHeader className={`flex flex-row items-center justify-between space-y-0 pb-2 ${isMobile ? "p-3" : ""}`}>
          <CardTitle className={`${isMobile ? "text-xs" : "text-sm"} font-medium`}>Today</CardTitle>
          <Calendar className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent className={isMobile ? "p-3 pt-0" : ""}>
          <div className={`${isMobile ? "text-xl" : "text-2xl"} font-bold`}>
            {stats.todayHours}h {stats.todayMinutes}m
          </div>
          <p className={`${isMobile ? "text-[10px]" : "text-xs"} text-muted-foreground mt-1`}>
            {format(new Date(), "EEEE, MMM d")}
          </p>
        </CardContent>
      </Card>

      <Card className="hover-scale shadow-md border-t-2 border-t-primary/20">
        <CardHeader className={`flex flex-row items-center justify-between space-y-0 pb-2 ${isMobile ? "p-3" : ""}`}>
          <CardTitle className={`${isMobile ? "text-xs" : "text-sm"} font-medium`}>Total Time</CardTitle>
          <Clock className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent className={isMobile ? "p-3 pt-0" : ""}>
          <div className={`${isMobile ? "text-xl" : "text-2xl"} font-bold`}>
            {stats.totalHours}h {stats.totalMinutes}m
          </div>
          <p className={`${isMobile ? "text-[10px]" : "text-xs"} text-muted-foreground mt-1`}>
            Across {stats.totalEntries} entries
          </p>
          {Object.keys(stats.projectStats).length > 0 && (
            <div className="mt-4 space-y-2 max-h-24 overflow-y-auto scrollbar-thin">
              {Object.entries(stats.projectStats).map(([projectId, minutes]) => {
                const project = projects.find(p => p.id === projectId);
                if (!project) return null;
                return (
                  <div key={projectId} className="flex justify-between text-sm">
                    <span className={`text-muted-foreground truncate ${isMobile ? "max-w-[55%] text-xs" : "max-w-[60%]"}`}>
                      {project.name}
                    </span>
                    <span className={isMobile ? "text-xs" : ""}>
                      {Math.floor(minutes / 60)}h {minutes % 60}m
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="hover-scale shadow-md border-t-2 border-t-primary/20">
        <CardHeader className={`flex flex-row items-center justify-between space-y-0 pb-2 ${isMobile ? "p-3" : ""}`}>
          <CardTitle className={`${isMobile ? "text-xs" : "text-sm"} font-medium`}>Total Billing</CardTitle>
          <DollarSign className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent className={isMobile ? "p-3 pt-0" : ""}>
          <div className={`${isMobile ? "text-xl" : "text-2xl"} font-bold`}>
            ${stats.totalBilling.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <p className={`${isMobile ? "text-[10px]" : "text-xs"} text-muted-foreground mt-1`}>
            {stats.selectedProject?.pricingComponents?.length 
              ? "Multiple pricing components" 
              : `Base rate: $${stats.hourlyRate.toLocaleString('en-US')}/hour`}
          </p>
          
          {stats.selectedProject?.pricingComponents && stats.selectedProject.pricingComponents.length > 0 && (
            <div className="mt-3 space-y-1.5 max-h-24 overflow-y-auto scrollbar-thin pt-2 border-t">
              {stats.selectedProject.pricingComponents.map(comp => (
                <div key={comp.id} className="flex justify-between text-xs">
                  <span className={`text-muted-foreground truncate ${isMobile ? "max-w-[55%] text-[10px]" : "max-w-[60%]"}`}>
                    {comp.name || "Unnamed component"}
                  </span>
                  <span className={isMobile ? "text-[10px]" : ""}>
                    {comp.pricingType === 'hourly' 
                      ? `$${comp.amount.toLocaleString('en-US')}/hr` 
                      : `$${comp.amount.toLocaleString('en-US')} (fixed)`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
